import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Card = styled(motion.a)`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  overflow: hidden;
  background: var(--surface);
  border: 1px solid var(--border);
  text-decoration: none;
  color: inherit;
  transition: transform 0.35s ease, border-color 0.3s ease, box-shadow 0.35s ease;
  isolation: isolate;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ $accent }) => $accent};
    opacity: 0.1;
    z-index: -1;
    transition: opacity 0.35s ease;
  }
  &:hover {
    transform: translateY(-6px);
    border-color: var(--border-strong);
    box-shadow: 0 30px 60px -30px rgba(0, 0, 0, 0.7);
  }
  &:hover::before {
    opacity: 0.22;
  }
`;

const Art = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: ${({ $accent }) => $accent};
`;

const ArtImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
  ${Card}:hover & {
    transform: scale(1.06);
  }
`;

const Pill = styled.span`
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 2;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(7, 6, 15, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Fade = styled.div`
  position: absolute;
  inset: auto 0 0 0;
  height: 40%;
  background: linear-gradient(180deg, transparent, rgba(7, 6, 15, 0.55));
  z-index: 1;
  pointer-events: none;
`;

const Body = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
`;

const Title = styled.h3`
  margin: 0;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.35rem;
  letter-spacing: -0.01em;
`;

const Tagline = styled.p`
  margin: -10px 0 0;
  color: var(--text-mute);
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
`;

const Desc = styled.p`
  margin: 0;
  color: var(--text-dim);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid var(--border);
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-dim);
  font-size: 0.9rem;
  font-weight: 500;
`;

const PlayLink = styled.span`
  color: var(--text);
  font-weight: 600;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  svg {
    transition: transform 0.25s ease;
  }
  ${Card}:hover & svg {
    transform: translateX(4px);
  }
`;

export default function GameCard({ game, index = 0 }) {
  return (
    <Card
      href={game.url}
      target="_blank"
      rel="noopener noreferrer"
      $accent={game.accent}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <Art $accent={game.accent}>
        <Pill>{game.category}</Pill>
        <ArtImg src={game.image} alt={`${game.title} — ${game.tagline}`} loading="lazy" />
        <Fade />
      </Art>
      <Body>
        <Title>{game.title}</Title>
        <Tagline>{game.tagline}</Tagline>
        <Desc>{game.description}</Desc>
        <Meta>
          <Stat>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 17.3l-6.16 3.7 1.64-7.03L2 9.24l7.19-.61L12 2l2.81 6.63 7.19.61-5.48 4.73L18.16 21z" />
            </svg>
            {game.rating}
          </Stat>
          <Stat>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {game.downloads}
          </Stat>
          <PlayLink>
            Get on Play
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </PlayLink>
        </Meta>
      </Body>
    </Card>
  );
}
